import type { DocumentTemplateFileDescribedPayload } from '@Application/Documents/TemplateFiles/Actions/documentTemplateFileDescribed';
import DocumentTemplateFilePreviewFieldView from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/Views/DocumentTemplateFilePreviewFieldView';
import DocumentTemplateFilePreviewNodeView from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/Views/DocumentTemplateFilePreviewNodeView';
import DeclarationGroupView from '@UI/Pages/DocumentTemplates/Shared/Declaration/DeclarationGroupView';
import type LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';

/**
 * `loading` and `readFailed` have no equivalent on the server's decision rule: they only describe the
 * local flow of picking a file in this sidebar (reading in progress, or the read itself failed before
 * any report could be produced) and never reach the store.
 */
export type DocumentTemplateFilePreviewStatus =
	| 'loading'
	| 'readFailed'
	| DocumentTemplateFileDescribedPayload['status'];

type DocumentTemplateFilePreviewArgs = {
	readonly status: DocumentTemplateFilePreviewStatus;
	readonly fieldCount: number;
	readonly nodes: readonly DocumentTemplateFilePreviewNodeView[];
	readonly fields: readonly DocumentTemplateFilePreviewFieldView[];
	readonly readFailureMessage: string | null;
};

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor. Lives next to the
 * sidebar that reads it, colocated under its own `Views/` — no store carries it: the local preview is
 * read straight off `DOCUMENT_TEMPLATE_FILE_DESCRIBED`, never persisted.
 */
class DocumentTemplateFilePreviewView {
	readonly status: DocumentTemplateFilePreviewStatus;
	readonly fieldCount: number;
	readonly nodes: readonly DocumentTemplateFilePreviewNodeView[];
	readonly fields: readonly DocumentTemplateFilePreviewFieldView[];
	readonly readFailureMessage: string | null;

	constructor(args: DocumentTemplateFilePreviewArgs) {
		this.status = args.status;
		this.fieldCount = args.fieldCount;
		this.nodes = args.nodes;
		this.fields = args.fields;
		this.readFailureMessage = args.readFailureMessage;

		Object.freeze(this);
	}

	/** Only a `declared` preview lets the deposit go through — zero variables included: the deposit
	 * confort-check never blocks on an empty declaration, only on an unreadable or malformed one. */
	get isDeclared(): boolean {
		return this.status === 'declared';
	}

	/**
	 * The declaration regrouped for reading as a tree: the root section when variables sit outside any
	 * node, then the first-level nodes, each nesting its sub-groups and variables. A declared node with
	 * no variable keeps its place: that it declares nothing is an answer.
	 */
	get sections(): readonly DeclarationGroupView[] {
		return DeclarationGroupView.buildSections(this.nodes, this.fields);
	}

	static loading(): DocumentTemplateFilePreviewView {
		return new DocumentTemplateFilePreviewView({
			status: 'loading',
			fieldCount: 0,
			nodes: [],
			fields: [],
			readFailureMessage: null,
		});
	}

	static readFailed(message: string): DocumentTemplateFilePreviewView {
		return new DocumentTemplateFilePreviewView({
			status: 'readFailed',
			fieldCount: 0,
			nodes: [],
			fields: [],
			readFailureMessage: message,
		});
	}

	static fromDescribedPayload(
		payload: DocumentTemplateFileDescribedPayload,
		resolveFieldType: (value: string) => LabeledValueView,
	): DocumentTemplateFilePreviewView {
		if (payload.status !== 'declared') {
			return new DocumentTemplateFilePreviewView({
				status: payload.status,
				fieldCount: 0,
				nodes: [],
				fields: [],
				readFailureMessage: null,
			});
		}

		return new DocumentTemplateFilePreviewView({
			status: 'declared',
			fieldCount: payload.fieldCount,
			nodes: payload.nodes.map((node) => new DocumentTemplateFilePreviewNodeView(node)),
			fields: payload.fields.map(
				(field) => new DocumentTemplateFilePreviewFieldView(field, resolveFieldType),
			),
			readFailureMessage: null,
		});
	}
}

export default DocumentTemplateFilePreviewView;
